import { createQueryKeys } from "@lukemorales/query-key-factory";

import { createServiceResponseSchema, privateApi } from "@/api/axios";
import { createCareplanCartDataModel } from "@/domains/careplan/factories/create-careplan-cart-data-model";
import { createCareplanSellingPlansDataModel } from "@/domains/careplan/factories/create-careplan-selling-plans-data-model";
import careplanAvailableSellingPlansQuery from "@/domains/careplan/models/graphql/careplan-available-selling-plans.graphql?raw";
import cartQuery from "@/domains/careplan/models/graphql/cart-query.graphql?raw";
import { careplanDataModelSchema, careplanMetaSchema } from "@/domains/careplan/models/schemas";
import { storefrontApi } from "@/modules/network";
import type {
  GetCartQuery,
  GetCartQueryVariables,
  GetProductSellingPlansQuery,
  GetProductSellingPlansQueryVariables,
} from "@/types/storefront-api.gen";

const getCurrentCareplan = async () => {
  const response = await privateApi.get("/careplans");

  const parsed = createServiceResponseSchema(careplanDataModelSchema, careplanMetaSchema).parse(
    response.data,
  );

  return {
    ...parsed.data,
    ...parsed.meta,
  };
};

const getCurrentCareplanCart = async () => {
  const cartId = sessionStorage.getItem("careplanCartId");
  if (!cartId) {
    return null;
  }

  const variables: GetCartQueryVariables = { cartId };
  const response = await storefrontApi.request<GetCartQuery>(cartQuery, {
    variables,
  });

  if (response.errors) {
    throw new Error(response.errors.message);
  }

  return createCareplanCartDataModel(response.data);
};

const getCartAvailableSellingPlans = async (cartVariantIds: string[]) => {
  const variables: GetProductSellingPlansQueryVariables = { ids: cartVariantIds };
  const response = await storefrontApi.request<GetProductSellingPlansQuery>(
    careplanAvailableSellingPlansQuery,
    { variables },
  );

  if (response.errors) {
    throw new Error(response.errors.message);
  }

  return createCareplanSellingPlansDataModel(response.data, cartVariantIds);
};

export const careplanQueryOptions = createQueryKeys("careplan", {
  current: {
    queryKey: null,
    queryFn: () => {
      return getCurrentCareplan();
    },
  },
  cart: {
    queryKey: null,
    queryFn: () => {
      return getCurrentCareplanCart();
    },
  },
  sellingPlans: (cartVariantIds: string[]) => {
    return {
      queryKey: [cartVariantIds],
      queryFn: () => {
        return getCartAvailableSellingPlans(cartVariantIds);
      },
    };
  },
});
